import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { BaseTicketActionProps } from "./types";
import { EmailContextCard } from "../context-cards";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { Badge, Button, Combobox, ComboboxOption, htmlToText, Input, Label, RichTextEditor } from "@tellescope/ui";
import { formatName, reduceToValuesForKey } from "@tellescope/utils";
import { Mail, X } from "lucide-react";

interface InboxEmailRecipientItemProps {
  enduser?: TellescopeModelFromName<"endusers">;
  emailAddress?: string;
}

const InboxEmailRecipientItem = ({ enduser, emailAddress }: InboxEmailRecipientItemProps) => {
  if (!enduser) return null;

  const displayName = formatName(enduser);
  const displayEmail = emailAddress || enduser.email;

  return (
    <div className="flex gap-2 items-center">
      <div className="flex flex-col">
        <p className="font-semibold">{displayName}</p>
        {displayEmail && <p className="text-sm text-muted-foreground">{displayEmail}</p>}
      </div>
    </div>
  );
};

export interface SendEmailActionProps extends BaseTicketActionProps<"Send Email"> {
  onSend: () => void;
}

export const SendEmailAction = ({ completedAt, info, onSend, enduserId }: SendEmailActionProps) => {
  const { t } = useTranslation("tickets");
  const [enduserSearch, setEnduserSearch] = useState("");
  const [ccEnduserSearch, setCcEnduserSearch] = useState("");
  const [ccEndusers, setCcEndusers] = useState<TellescopeModelFromName<"endusers">[]>([]);

  const templateQuery = useTellescopeQuery({
    model: "templates",
    method: "getOne",
    request: info.templateId ?? "",
    enabled: !!info.templateId && !completedAt,
  });

  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: enduserSearch
        ? {
            query: enduserSearch,
          }
        : undefined,
    },
    select: (data) => data.pages.flat(),
    enabled: !completedAt,
  });

  const ccEndusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: ccEnduserSearch
        ? {
            query: ccEnduserSearch,
          }
        : undefined,
    },
    select: (data) => data.pages.flat(),
    enabled: !completedAt,
  });

  const sendEmailMutation = useTellescopeMutation({
    model: "emails",
    method: "createOne",

    onSuccess: () => {
      onSend();
    },
  });

  const form = useForm({
    fields: {
      enduserId: {
        type: "text",
        initialValue: enduserId ?? "",
        required: true,
      },
      emailAddress: {
        type: "text",
        initialValue: "",
        required: false,
      },
      subject: {
        type: "text",
        initialValue: templateQuery.data?.subject ?? "",
        required: true,
      },
      message: {
        type: "textarea",
        initialValue: templateQuery.data?.message ?? "",
        required: true,
      },
    },
    validators: {
      message: (value) => (value.length > 0 ? undefined : t("Email cannot be empty")),
    },
    validateOn: "change",
    onSubmit: async (values) => {
      if (!values.enduserId || !values.subject) return;

      try {
        const ccEmailAddresses = reduceToValuesForKey(ccEndusers, "email", true);

        await sendEmailMutation.mutateAsync({
          enduserId: values.enduserId,
          alternateToAddress:
            values.emailAddress !== selectedEnduserQuery.data?.email ? values.emailAddress : undefined,
          cc: ccEmailAddresses.length > 0 ? ccEmailAddresses : undefined,
          isMarketing: false,
          HTMLContent: values.message,
          textContent: htmlToText(values.message),
          subject: values.subject,
        });
      } catch (error) {
        console.error("Failed to send email:", error);
      }
    },
  });

  const selectedEnduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: form.values.enduserId,
    enabled: !!enduserId && !completedAt,
  });

  const availableEmails = useMemo(() => {
    if (!selectedEnduserQuery.data) return [];

    const emails: { value: string; label: string; isPrimary: boolean }[] = [];

    if (selectedEnduserQuery.data.email) {
      emails.push({
        value: selectedEnduserQuery.data.email,
        label: `${selectedEnduserQuery.data.email} (Primary)`,
        isPrimary: true,
      });
    }

    if (selectedEnduserQuery.data.alternateEmails) {
      selectedEnduserQuery.data.alternateEmails.forEach((email) => {
        emails.push({
          value: email,
          label: email,
          isPrimary: false,
        });
      });
    }

    return emails;
  }, [selectedEnduserQuery.data]);

  const handleRemoveCcEnduser = useCallback(
    (enduserId: string) => {
      const updatedCcEndusers = ccEndusers.filter((enduser) => enduser.id !== enduserId);
      setCcEndusers(updatedCcEndusers);
    },
    [ccEndusers]
  );

  if (completedAt && info.emailId) {
    return <EmailContextCard emailId={info.emailId} />;
  }

  const messageFieldProps = form.getCustomFieldProps("message");

  return (
    <div className="space-y-4 border border-gray-200 rounded-lg bg-white">
      <div className="flex items-center justify-between bg-gray-100 p-4 border-b border-b-gray-200">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Mail className="h-4 w-4 text-gray-600 flex-shrink-0" />
          <h3 className="text-sm font-medium text-gray-900 truncate">{t("Send Email")}</h3>
        </div>
      </div>

      <form className="space-y-4 p-4" {...form.getFormProps()}>
        {/* To - Enduser Selector */}
        <div className="space-y-2">
          <Label htmlFor="recipient">{t("To")}</Label>
          <Combobox
            value={{
              value: form.getFieldProps("enduserId").value,
              label: selectedEnduserQuery.data ? formatName(selectedEnduserQuery.data) : "",
              metadata: { enduserId: form.getFieldProps("enduserId").value },
            }}
            formatSelectedValue={({ label }) => <p>{label}</p>}
            onChange={(newVal) => {
              if (typeof newVal?.metadata?.enduserId === "string") {
                const enduser = endusersQuery.data?.find(({ id }) => id === newVal.metadata?.enduserId);
                form.setFieldValue("enduserId", newVal?.metadata?.enduserId);
                form.setFieldValue("emailAddress", enduser?.email ?? "");
              } else {
                form.setFieldValue("enduserId", "");
                form.setFieldValue("emailAddress", "");
              }
            }}
            formatOption={(props) => (
              <InboxEmailRecipientItem
                enduser={endusersQuery.data?.find((enduser) => enduser.id === props.option.metadata?.enduserId)}
              />
            )}
            searchQuery={enduserSearch}
            setSearchQuery={setEnduserSearch}
            customFilter={(_, options) => options}
            placeholder={t("Select recipient...")}
            options={
              endusersQuery.data?.map<ComboboxOption<string>>((enduser) => ({
                value: enduser.id,
                label: formatName(enduser),
                metadata: {
                  enduserId: enduser.id,
                },
              })) ?? []
            }
          />
        </div>

        {/* Email Address Selector */}
        {selectedEnduserQuery.data && availableEmails.length > 0 && (
          <div className="space-y-2">
            <Label htmlFor="emailAddress">{t("Email Address")}</Label>
            <Combobox
              value={{
                value: form.getFieldProps("emailAddress").value,
                label: availableEmails.find((e) => e.value === form.getFieldProps("emailAddress").value)?.label ?? "",
                metadata: { emailAddress: form.getFieldProps("emailAddress").value },
              }}
              formatSelectedValue={({ label }) => <p>{label}</p>}
              onChange={(newVal) => {
                if (typeof newVal?.metadata?.emailAddress === "string") {
                  form.setFieldValue("emailAddress", newVal.metadata.emailAddress);
                }
              }}
              placeholder={t("Select email address...")}
              options={
                availableEmails.map<ComboboxOption<string>>((email) => ({
                  value: email.value,
                  label: email.label,
                  metadata: {
                    emailAddress: email.value,
                  },
                })) ?? []
              }
            />
          </div>
        )}

        {/* CC Selector */}
        <div className="space-y-2">
          <Label htmlFor="cc">{t("CC")}</Label>
          <Combobox
            value={undefined}
            formatSelectedValue={() => null}
            onChange={(newVal) => {
              if (typeof newVal?.metadata?.enduserId === "string") {
                const enduser = ccEndusersQuery.data?.find(({ id }) => id === newVal.metadata?.enduserId);
                if (enduser && !ccEndusers.find((e) => e.id === enduser.id)) {
                  const updatedCcEndusers = [...ccEndusers, enduser];
                  setCcEndusers(updatedCcEndusers);
                }
              }
            }}
            formatOption={(props) => (
              <InboxEmailRecipientItem
                enduser={ccEndusersQuery.data?.find((enduser) => enduser.id === props.option.metadata?.enduserId)}
              />
            )}
            searchQuery={ccEnduserSearch}
            setSearchQuery={setCcEnduserSearch}
            customFilter={(_, options) => options}
            placeholder={t("Add CC recipients...")}
            options={
              ccEndusersQuery.data?.map<ComboboxOption<string>>((enduser) => ({
                value: enduser.id,
                label: formatName(enduser),
                metadata: {
                  enduserId: enduser.id,
                },
              })) ?? []
            }
          />

          {/* CC Recipients Chips */}
          {ccEndusers.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {ccEndusers.map((enduser) => (
                <Badge key={enduser.id} variant="secondary" className="gap-1">
                  {formatName(enduser)}
                  <button
                    type="button"
                    onClick={() => handleRemoveCcEnduser(enduser.id)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Subject */}
        <div className="space-y-2">
          <Label htmlFor="subject">{t("Subject")}</Label>
          <Input
            id="subject"
            type="text"
            placeholder={t("Enter subject")}
            {...form.getFieldProps("subject")}
            className="h-9"
          />
        </div>

        {/* Message */}
        <div className="space-y-2">
          <Label htmlFor="message">{t("Message")}</Label>
          <RichTextEditor
            content={messageFieldProps.value}
            onChange={messageFieldProps.onValueChange}
            onBlur={messageFieldProps.onBlur}
            placeholder={t("Write your message...")}
            className="w-full"
          />
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={!form.isValid || sendEmailMutation.isPending}>
            {sendEmailMutation.isPending ? t("Sending...") : t("Send Email")}
          </Button>
        </div>
      </form>
    </div>
  );
};
